import { useEffect, useRef, useState, useCallback } from "react";
import { statusPool } from "../lib/lifeos";

const TICK = 700; // ms
const rand = (a, b) => a + Math.random() * (b - a);

export function useSimulation(agents, deptById) {
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [live, setLive] = useState({});
  const [feed, setFeed] = useState([]);

  const runtime = useRef({});
  const pausedRef = useRef(paused);
  const speedRef = useRef(speed);
  const deptRef = useRef(deptById);
  const agentsRef = useRef(agents);

  pausedRef.current = paused;
  speedRef.current = speed;
  deptRef.current = deptById;
  agentsRef.current = agents;

  const pushFeed = useCallback((entry) => {
    setFeed((prev) => [...prev, entry].slice(-50));
  }, []);

  // Sync runtime entries with current agents
  useEffect(() => {
    const rt = runtime.current;
    agents.forEach((a) => {
      if (!rt[a.id]) {
        rt[a.id] = { phase: "idle", timer: rand(1, 4), status: "Idle", cycles: 0, progress: 0, override: null };
      }
    });
    Object.keys(rt).forEach((id) => {
      if (!agents.find((a) => a.id === id)) delete rt[id];
    });
  }, [agents]);

  const injectTask = useCallback((agentId, steps, result) => {
    const rt = runtime.current[agentId];
    if (!rt) return;
    rt.override = { steps: [...steps], result, index: 0 };
    rt.phase = "walking";
    rt.timer = 1.5;
    rt.status = "Heading to workstation...";
    rt.progress = 0;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (pausedRef.current) return;
      const rt = runtime.current;
      const dt = (TICK / 1000) * speedRef.current;
      let changed = false;

      Object.keys(rt).forEach((id) => {
        const s = rt[id];
        const agent = agentsRef.current.find((a) => a.id === id);
        if (!agent) return;
        const dept = deptRef.current[agent.department_id];
        const deptName = dept ? dept.name : "Operations";
        const deptCode = dept ? dept.code : "";
        s.timer -= dt;

        if (s.phase === "idle") {
          if (s.timer <= 0) {
            s.phase = "walking";
            s.timer = 1.6;
            s.status = "Walking to workstation...";
            changed = true;
          }
        } else if (s.phase === "walking") {
          if (s.timer <= 0) {
            s.phase = "working";
            s.progress = 0;
            if (s.override) {
              s.status = s.override.steps[0] || "Working...";
              s.override.index = 0;
              s.timer = 2.6;
              s.cycles = s.override.steps.length;
            } else {
              const pool = statusPool(deptName);
              s.status = pool[Math.floor(Math.random() * pool.length)];
              s.cycles = Math.floor(rand(3, 6));
              s.timer = 3;
            }
            changed = true;
          }
        } else if (s.phase === "working") {
          const totalCycles = s.override ? s.override.steps.length : s.cycles;
          s.progress = Math.min(100, s.progress + (dt / (totalCycles * 3)) * 100);
          if (s.timer <= 0) {
            if (s.override) {
              s.override.index += 1;
              if (s.override.index >= s.override.steps.length) {
                s.phase = "complete";
                s.status = "Task Complete \u2713";
                s.timer = 3;
                s.progress = 100;
              } else {
                s.status = s.override.steps[s.override.index];
                s.timer = 2.6;
              }
            } else {
              s.cycles -= 1;
              if (s.cycles <= 0) {
                s.phase = "complete";
                s.status = "Task Complete \u2713";
                s.timer = 2.4;
                s.progress = 100;
              } else {
                const pool = statusPool(deptName);
                s.status = pool[Math.floor(Math.random() * pool.length)];
                s.timer = 3;
              }
            }
            changed = true;
          }
        } else if (s.phase === "complete") {
          if (s.timer <= 0) {
            if (!s._logged) {
              pushFeed({
                id: `${id}-${Date.now()}`,
                agent_id: id,
                agent_name: agent.name,
                department_code: deptCode,
                message: s.override
                  ? `${agent.name} finished: ${s.override.result.slice(0, 70)}`
                  : `${agent.name} completed a ${deptName.toLowerCase()} task`,
                level: "success",
                created_at: new Date().toISOString(),
                ephemeral: true,
              });
            }
            s.override = null;
            s._logged = false;
            s.phase = "idle";
            s.timer = rand(2, 5);
            s.status = "Idle";
            s.progress = 0;
            changed = true;
          }
        }
      });

      if (changed || Math.random() < 0.3) {
        const snap = {};
        Object.keys(rt).forEach((id) => {
          const s = rt[id];
          snap[id] = { phase: s.phase, status: s.status, progress: Math.round(s.progress) };
        });
        setLive(snap);
      }
    }, TICK);
    return () => clearInterval(interval);
  }, [pushFeed]);

  return { live, feed, paused, setPaused, speed, setSpeed, injectTask };
}
